/**
 * inlineHelp.js — Встроенная интерактивная справка
 * При первом использовании инструмента/панели — всплывающая подсказка.
 * Подсказки показываются один раз, запоминаются в localStorage.
 */

import { log } from '../utils/logger.js';
import { showToast } from '../utils/helpers.js';

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();

const STORAGE_KEY = 'mikhmel_help_seen';
let _seen = {};

// Загрузить из localStorage
try { _seen = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch (e) { log.debug('[InlineHelp]', e.message); }

function _markSeen(id) {
  _seen[id] = true;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_seen)); } catch (e) { log.debug('[InlineHelp]', e.message); }
}

// ══════════════════════════════════════════════════════════════
//  СПРАВОЧНЫЕ ТЕКСТЫ
// ══════════════════════════════════════════════════════════════

const HELP = {
  // Инструменты
  tool_WindowLevel: { title: 'Окно/Уровень (W/L)', text: 'Тяните мышью: горизонтально = ширина окна, вертикально = уровень. На мобильном — одним пальцем.' },
  tool_Pan: { title: 'Перемещение', text: 'Зажмите и тяните для перемещения изображения. На мобильном — 3 пальца.' },
  tool_Zoom: { title: 'Масштаб', text: 'Тяните вверх/вниз для масштабирования. На мобильном — щипок двумя пальцами.' },
  tool_StackScroll: { title: 'Листание срезов', text: 'Колесо мыши или свайп вверх/вниз. В MPR — прокрутка через стопку срезов.' },
  tool_Length: { title: 'Линейка', text: 'Кликните начальную и конечную точку. Отображается расстояние в мм. Для удаления — Del.' },
  tool_Angle: { title: 'Измерение угла', text: 'Три клика: вершина, первый луч, второй луч. Показывает угол в градусах.' },
  tool_EllipticalROI: { title: 'Эллипс ROI', text: 'Нарисуйте эллипс. Показывает: площадь, средний HU, std, мин/макс.' },
  tool_RectangleROI: { title: 'Прямоугольник ROI', text: 'Нарисуйте прямоугольник для анализа области. Показывает статистику HU.' },
  tool_Bidirectional: { title: 'Двунаправленное измерение', text: 'Два перпендикулярных диаметра — для оценки размера опухоли по RECIST.' },
  tool_PlanarFreehandROI: { title: 'Свободное рисование', text: 'Рисуйте произвольный контур. Показывает площадь и статистику HU внутри.' },
  tool_Crosshairs: { title: 'Перекрестие', text: 'Синхронизация MPR: клик на одном viewport — прокрутка остальных к этой точке. Требует ≥2 viewport.' },
  tool_TrackballRotate: { title: '3D Вращение', text: 'Вращение 3D-объёма. Только для Volume 3D viewport.' },
  tool_Magnify: { title: 'Лупа', text: 'Увеличение области под курсором. Работает только в 2D режиме.' },
  tool_Probe: { title: 'Зонд', text: 'Кликните на пиксель — покажет HU значение в этой точке.' },
  tool_CobbAngle: { title: 'Угол Кобба', text: 'Для оценки сколиоза: проведите две линии вдоль верхнего и нижнего позвонков.' },

  // Панели
  panel_ai: { title: 'ИИ-анализ', text: 'Автоматическая сегментация, детекция узлов, классификация. Модели загружаются при первом вызове.' },
  panel_seg: { title: 'Сегментация', text: 'Ручная сегментация кистью, заливкой. Создание масок для анализа объёмов.' },
  panel_render: { title: '3D / Кинематика', text: 'Cinematic rendering с пресетами: кости, сосуды, мягкие ткани. Настройка теней и освещения.' },
  panel_clinical: { title: 'Клинические инструменты', text: 'Agatston Score, CTR, эмфизема, BMD, пневмоторакс, диаметр аорты — автоматические расчёты.' },
  panel_fusion: { title: 'Совмещение', text: 'Наложение двух серий (PET+CT). Управление прозрачностью и цветовой картой.' },
  panel_measure: { title: 'Измерения', text: 'Все аннотации и измерения текущей серии. Экспорт/импорт аннотаций.' },
  panel_report: { title: 'Отчёт', text: 'Формирование отчёта по исследованию. Экспорт скриншотов и данных.' },

  // Радиохирургия (полные статьи — в справочнике RT, кнопка «📖 Справочник»)
  rt_gtv:      { title: 'GTV / CTV / PTV', text: 'GTV — видимая опухоль. CTV — плюс микроскопическое распространение. PTV — плюс геометрический запас на неточность укладки. Подробно: панель RT → 📖 Справочник.' },
  rt_margin:   { title: 'Отступ в радиохирургии', text: 'В СРХ отступ 0–2 мм (в обычной лучевой терапии 5–10 мм) — жёсткая фиксация и контроль по снимкам убирают технические неточности.' },
  rt_volume:   { title: 'Объём мишени', text: 'Считается по методу Кавальери: сумма площадей контуров × шаг срезов. Не требует предположений о форме. Обводите от первого среза, где очаг появился, до последнего, где исчез, — важно не число обводок, а то, что они дошли до краёв. Очаг 17 мм на срезах 6 мм виден дважды, и двух контуров хватает.' },
  rt_reformat: { title: 'Реформаты и точность', text: 'Оконтуривать по реформатам можно — контуры хранятся в мм пациента. Но геометрическая основа плана СРХ — КТ: у МРТ есть искажения 1–2 мм.' },
  rt_rtstruct: { title: 'RTSTRUCT', text: 'DICOM-файл с контурами структур в координатах пациента. Открывается в любой станции с той же серией.' },

  // Режимы
  mode_mpr: { title: 'MPR (4 viewport)', text: 'Axial + Sagittal + Coronal + 3D Volume. Стандартный режим для КТ/МРТ.' },
  mode_2d: { title: '2D Stack', text: 'Одиночный viewport, листание через стопку срезов. Для рентгенов и одиночных серий.' },
  mode_3d: { title: '3D Volume', text: 'Объёмный рендеринг. Пресеты: кости, мягкие ткани, MIP.' },
};

// ══════════════════════════════════════════════════════════════
//  ПОКАЗ ПОДСКАЗКИ
// ══════════════════════════════════════════════════════════════

let _activeTooltip = null;

function _showHelp(id, anchorEl) {
  if (_seen[id]) return;
  if (_activeTooltip) _activeTooltip.remove();

  const help = HELP[id];
  if (!help) return;

  const tip = document.createElement('div');
  tip.className = 'mikhmel-help-tip';
  tip.innerHTML = `
    <div style="font-weight:600;font-size:13px;margin-bottom:4px;color:var(--cyan,#67e8f9);">${help.title}</div>
    <div style="font-size:11px;line-height:1.5;color:var(--text-primary,#e0e0e0);">${help.text}</div>
    <button class="mikhmel-help-close" style="
      margin-top:8px;padding:4px 12px;border:none;border-radius:6px;
      background:var(--accent,#3b82f6);color:#fff;font-size:11px;cursor:pointer;
    ">Понятно</button>
  `;

  // Позиционирование
  const rect = anchorEl?.getBoundingClientRect();
  const isMobile = window.innerWidth <= 768;

  if (isMobile) {
    tip.style.cssText = `
      position:fixed;bottom:70px;left:10px;right:10px;
      background:rgba(18,18,30,0.97);border:1px solid var(--accent,#3b82f6);
      border-radius:12px;padding:14px;z-index:10002;
      box-shadow:0 -4px 20px rgba(0,0,0,0.5);
      animation:helpFadeIn 0.3s ease;
    `;
  } else if (rect) {
    const top = Math.min(rect.bottom + 8, window.innerHeight - 150);
    const left = Math.min(rect.left, window.innerWidth - 300);
    tip.style.cssText = `
      position:fixed;top:${top}px;left:${left}px;width:280px;
      background:rgba(18,18,30,0.97);border:1px solid var(--accent,#3b82f6);
      border-radius:10px;padding:12px;z-index:10002;
      box-shadow:0 8px 24px rgba(0,0,0,0.5);
      animation:helpFadeIn 0.3s ease;
    `;
  } else {
    tip.style.cssText = `
      position:fixed;bottom:80px;right:20px;width:280px;
      background:rgba(18,18,30,0.97);border:1px solid var(--accent,#3b82f6);
      border-radius:10px;padding:12px;z-index:10002;
      box-shadow:0 8px 24px rgba(0,0,0,0.5);
    `;
  }

  tip.querySelector('.mikhmel-help-close').addEventListener('click', () => {
    tip.remove();
    _activeTooltip = null;
    _markSeen(id);
  }, { signal: _ac.signal });

  // Автоскрытие через 15 секунд
  setTimeout(() => {
    if (tip.parentNode) {
      tip.remove();
      _activeTooltip = null;
      _markSeen(id);
    }
  }, 15000);

  document.body.appendChild(tip);
  _activeTooltip = tip;
}

// ══════════════════════════════════════════════════════════════
//  PUBLIC API
// ══════════════════════════════════════════════════════════════

/** Показать подсказку для инструмента (вызывается из setActiveTool) */
export function showToolHelp(toolName, anchorEl) {
  _showHelp('tool_' + toolName, anchorEl);
}

/** Показать подсказку для панели (вызывается из switchMPanel) */
export function showPanelHelp(panelId, anchorEl) {
  _showHelp('panel_' + panelId, anchorEl);
}

/** Показать подсказку для режима */
export function showModeHelp(mode) {
  _showHelp('mode_' + mode);
}

/** Сбросить все подсказки (показать заново) */
export function resetAllHelp() {
  _seen = {};
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { log.debug('[InlineHelp]', e.message); }
}

/** Инициализация: CSS + привязка к событиям */
export function initInlineHelp() {
  // Inject CSS animation
  if (!document.getElementById('mikhmel-help-css')) {
    const style = document.createElement('style');
    style.id = 'mikhmel-help-css';
    style.textContent = `
      @keyframes helpFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      .mikhmel-help-tip { font-family: inherit; }
    `;
    document.head.appendChild(style);
  }

  log.debug('[Help] Inline help initialized');
}

// Глобальный доступ для onclick в HTML
// Функции экспортированы как ES-модули — window.* больше не нужны

// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Guided Walkthrough + Search
// ══════════════════════════════════════════════════════════════

const WALKTHROUGH_STEPS = [
  { target: '#viewport-container', text: 'Основной вьюпорт — сюда загружаются DICOM-изображения. Колесо мыши для прокрутки.', position: 'center' },
  { target: '.sidebar-left', text: 'Левая панель — список серий, DICOM-теги, закладки.', position: 'right' },
  { target: '.sidebar-right', text: 'Правая панель — W/L пресеты, 3D настройки, инструменты.', position: 'left' },
  { target: '.topbar', text: 'Верхняя панель — файлы, раскладка, правка, экспорт.', position: 'bottom' },
  // [11.0.1] Шаг обзора про кнопку «Enterprise» снят: кнопки нет, панели нет.
];
let _walkthroughStep = -1;
let _walkthroughEl = null;

/**
 * Start guided walkthrough — highlights UI areas step by step
 */
export function startWalkthrough() {
  _walkthroughStep = 0;
  _showWalkthroughStep();
}

/**
 * Advance to next walkthrough step
 */
export function nextWalkthroughStep() {
  _walkthroughStep++;
  if (_walkthroughStep >= WALKTHROUGH_STEPS.length) {
    stopWalkthrough();
    showToast('✅ Обзор завершён!', 'success', 2000);
    return;
  }
  _showWalkthroughStep();
}

/**
 * Stop walkthrough
 */
export function stopWalkthrough() {
  _walkthroughStep = -1;
  if (_walkthroughEl) { _walkthroughEl.remove(); _walkthroughEl = null; }
}

function _showWalkthroughStep() {
  if (_walkthroughEl) _walkthroughEl.remove();
  const step = WALKTHROUGH_STEPS[_walkthroughStep];
  if (!step) return;
  const target = document.querySelector(step.target);
  _walkthroughEl = document.createElement('div');
  _walkthroughEl.style.cssText = 'position:fixed;z-index:100000;background:rgba(10,10,20,0.96);border:2px solid #60a5fa;border-radius:12px;padding:16px 20px;color:#e0e0e0;font-size:13px;max-width:320px;box-shadow:0 12px 40px rgba(0,0,0,0.6)';
  if (target) {
    const rect = target.getBoundingClientRect();
    _walkthroughEl.style.top = Math.min(rect.bottom + 12, window.innerHeight - 200) + 'px';
    _walkthroughEl.style.left = Math.max(12, Math.min(rect.left, window.innerWidth - 340)) + 'px';
    target.style.outline = '2px solid #60a5fa';
    target.style.outlineOffset = '2px';
    setTimeout(() => { target.style.outline = ''; target.style.outlineOffset = ''; }, 3000);
  } else {
    _walkthroughEl.style.top = '50%'; _walkthroughEl.style.left = '50%'; _walkthroughEl.style.transform = 'translate(-50%,-50%)';
  }
  _walkthroughEl.innerHTML = `<div style="margin-bottom:10px;line-height:1.5">${step.text}</div>
    <div style="display:flex;gap:8px;justify-content:flex-end">
      <button onclick="this.closest('div').remove()" style="padding:4px 12px;border:1px solid #334155;background:transparent;color:#94a3b8;border-radius:6px;cursor:pointer;font-size:11px">Пропустить</button>
      <button id="_wt_next" style="padding:4px 16px;border:none;background:#3b82f6;color:#fff;border-radius:6px;cursor:pointer;font-size:11px">${_walkthroughStep < WALKTHROUGH_STEPS.length - 1 ? 'Далее →' : 'Готово ✓'}</button>
    </div>
    <div style="font-size:10px;color:#475569;margin-top:8px">${_walkthroughStep + 1} / ${WALKTHROUGH_STEPS.length}</div>`;
  _walkthroughEl.querySelector('#_wt_next').addEventListener('click', nextWalkthroughStep, { signal: _ac.signal });
  document.body.appendChild(_walkthroughEl);
}

/**
 * Search help topics by query
 * @param {string} query
 * @returns {Array<{id:string, title:string, text:string}>}
 */
export function searchHelp(query) {
  const q = query.toLowerCase();
  return Object.entries(HELP).filter(([, h]) =>
    h.title.toLowerCase().includes(q) || h.text.toLowerCase().includes(q)
  ).map(([id, h]) => ({ id, title: h.title, text: h.text }));
}

/** Get full help state */
export function getHelpState() {
  return { totalTopics: Object.keys(HELP).length, seenTopics: Object.keys(_seen).length, walkthroughActive: _walkthroughStep >= 0, walkthroughStep: _walkthroughStep };
}

/** Mark all help as seen */
export function markAllSeen() { Object.keys(HELP).forEach(k => _markSeen(k)); }

// [FIX] Cleanup all event listeners
export function destroyInlineHelp() {
  _ac.abort();
  _ac = new AbortController();
}
